import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSEO } from "@/hooks/useSEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MagazineHero from "@/components/home/MagazineHero";
import FeaturedArticle from "@/components/home/FeaturedArticle";
import CategorySection from "@/components/home/CategorySection";
import YoutubeVideos from "@/components/home/YoutubeVideos";
import HeatmapPreview from "@/components/home/HeatmapPreview";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import lochNessBathymetry from "@/assets/loch-ness-bathymetry.png";
import { sightings } from "@/data/sightingsData";

type BlogPost = Database['public']['Tables']['blog_posts']['Row'] & {
  author: Database['public']['Tables']['profiles']['Row'] | null;
};

const fetchBlogPosts = async () => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(`*, author:profiles(*)`)
    .order('published_at', { ascending: false })
    .limit(7);

  if (error) throw error;
  return data as BlogPost[];
};

const mapPost = (post: BlogPost) => ({
  title: post.title,
  excerpt: post.excerpt,
  image: post.image_url || "/placeholder.svg",
  author: post.author?.full_name || post.author?.username || "Anonymous Researcher",
  date: new Date(post.published_at || post.created_at).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  }),
  categories: ["Article"],
  slug: post.slug,
});

const Index = () => {
  const { data: posts, isLoading } = useQuery({
    queryKey: ['homePosts'],
    queryFn: fetchBlogPosts,
  });

  useSEO({
    title: "Loch Ness Story | Nessie Sightings, Research & History",
    description: "Discover the fascinating history of the Loch Ness Monster. Explore sightings from 565 AD to present day, scientific research, famous photographs, and the latest evidence about Nessie.",
    canonical: "/",
    ogImage: "/lovable-uploads/74638626-9ce3-4018-94b3-ec8980da02b9.png",
  });

  const blogArticles = (posts || []).map(mapPost);
  const sightingArticles = sightings.map(s => ({
    title: s.title,
    excerpt: s.description.slice(0, 200) + "…",
    image: s.image,
    author: "Loch Ness Story",
    date: s.date,
    categories: s.categories.slice(0, 2),
    slug: `__sighting__${s.id}`,
  }));
  // Put "How Big Is Loch Ness" article first as featured
  const heroArticle = sightingArticles.find(s => s.slug === "__sighting__how-big-is-loch-ness");
  const otherSightings = sightingArticles.filter(s => s.slug !== "__sighting__how-big-is-loch-ness");
  const allArticles = [...(heroArticle ? [heroArticle] : []), ...blogArticles, ...otherSightings];
  const featured = allArticles[0];
  const latestArticles = allArticles.slice(1, 4);
  const moreArticles = allArticles.slice(4, 7);
  const sightingsSection = otherSightings.slice(0, 3);

  // Loch Ness length: ~36.2 km

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow">
        <MagazineHero />

        <div className="container mx-auto px-4">
          {/* Featured Article */}
          {featured && !isLoading && (
            <section className="py-12">
              <p className="text-accent text-xs font-semibold tracking-[0.3em] uppercase mb-6">
                Featured
              </p>
              <FeaturedArticle
                title={featured.title}
                excerpt={featured.excerpt}
                image={featured.image}
                slug={featured.slug}
                author={featured.author}
                date={featured.date}
                category="Featured"
              />
            </section>
          )}

          {/* Latest Articles */}
          {latestArticles.length > 0 && (
            <CategorySection
              title="Latest Articles"
              description="Recent investigations, analysis, and stories from the loch."
              articles={latestArticles}
              viewAllLink="/blog"
              viewAllLabel="All Articles"
            />
          )}

          {/* More Stories */}
          {moreArticles.length > 0 && (
            <CategorySection
              title="More Stories"
              articles={moreArticles}
              viewAllLink="/blog"
              viewAllLabel="Browse Archive"
            />
          )}

          {/* Sightings & Research */}
          {sightingsSection.length > 0 && (
            <CategorySection
              title="Sightings & Research"
              description="Historical encounters, photographic evidence, and scientific investigation."
              articles={sightingsSection}
              viewAllLink="/blog"
              viewAllLabel="View All"
            />
          )}

          {/* Sightings Heatmap */}
          <HeatmapPreview />

          {/* YouTube Section */}
          <YoutubeVideos />

          {/* Loch Ness Bathymetric Map */}
          <div className="py-12 mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">The Loch</h2>
            <p className="text-muted-foreground mb-6">A bathymetric visualization of Scotland's most mysterious body of water.</p>
            <div className="h-px bg-border mb-8" />
            <div className="relative w-full max-w-4xl mx-auto">
              <img
                src={lochNessBathymetry}
                alt="Bathymetric visualization of Loch Ness showing depth contours and key locations"
                className="w-full rounded-xl border border-border"
              />
              {/* Scale bar */}
              <div className="absolute bottom-6 right-6 flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <div className="w-24 md:w-32 h-0.5 bg-white" />
                  <span className="text-white text-xs font-medium drop-shadow-lg">5 km</span>
                </div>
                <span className="text-white/70 text-[10px] drop-shadow-lg">Total length ≈ 36.2 km</span>
              </div>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Bathymetric contour map of Loch Ness showing depth profiles and key locations along its shores
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
